import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { X, Check, Upload, Sparkles } from 'lucide-react';
import { cn } from '../../lib/cn';
import Button from '../ui/Button';
import NoteCallout from '../ui/NoteCallout';
import NumberInput from '../ui/NumberInput';
import {
  fetchCsrfToken,
  fetchCalendar,
  checkAvailability,
  submitBooking,
  formatDate,
  toDateString,
  type AvailabilityResult,
  type CalendarData,
  type DateRange,
  type DocType,
} from '../../lib/bookingApi';

const MAX_FILE_BYTES = 5 * 1024 * 1024;

const inputClasses =
  'w-full box-border px-3.5 py-3 border border-hairline rounded-md font-sans text-[14.5px] text-ink bg-white transition-all duration-150 outline-none hover:border-dark-muted focus:border-olive focus:shadow-[0_0_0_3px_rgba(75,94,40,0.15)] placeholder:text-faint disabled:opacity-60';

const labelClasses = 'block text-[12.5px] font-semibold uppercase tracking-[0.08em] text-muted mb-1.5';
const hintClasses = 'block text-[12.5px] text-faint mt-1.5';
const errorClasses = 'block text-[12.5px] text-[#9B3A2E] mt-1.5';

const docMeta: { key: DocType; label: string }[] = [
  { key: 'aadhar', label: 'Aadhar Card' },
  { key: 'pan', label: 'PAN Card' },
  { key: 'passport', label: 'Passport' },
  { key: 'license', label: 'Driving License' },
];

export interface BookingModalInitial {
  name?: string;
  checkIn?: string;
  checkOut?: string;
  adults?: number;
  kids?: number;
}

interface BookingModalProps {
  onClose: () => void;
  initial?: BookingModalInitial;
  onDraftChange?: (draft: BookingModalInitial) => void;
}

export default function BookingModal({ onClose, initial, onDraftChange }: BookingModalProps) {
  // Backend session
  const [csrfToken, setCsrfToken] = useState('');
  const [calendar, setCalendar] = useState<CalendarData | null>(null);
  const [calendarError, setCalendarError] = useState(false);

  // Step 1 — dates + name
  const [name, setName] = useState(initial?.name ?? '');
  const [checkIn, setCheckIn] = useState(initial?.checkIn ?? '');
  const [checkOut, setCheckOut] = useState(initial?.checkOut ?? '');
  const [stepErrors, setStepErrors] = useState<{ name?: string; checkIn?: string; checkOut?: string }>({});
  const [checking, setChecking] = useState(false);
  const [availability, setAvailability] = useState<AvailabilityResult | null>(null);

  // Step 2 — guest details
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [stateCity, setStateCity] = useState('');
  const [adults, setAdults] = useState(initial?.adults ?? 1);
  const [kids, setKids] = useState(initial?.kids ?? 0);
  const [message, setMessage] = useState('');
  const [docNumbers, setDocNumbers] = useState<Partial<Record<DocType, string>>>({});
  const [docFiles, setDocFiles] = useState<Partial<Record<DocType, File>>>({});
  const [docErrors, setDocErrors] = useState<Partial<Record<DocType, string>>>({});
  const [selectedDocs, setSelectedDocs] = useState<DocType[]>([]);
  const fileInputs = useRef<Partial<Record<DocType, HTMLInputElement | null>>>({});

  // Aadhaar back side — a separate optional slot, not part of the generic
  // docFiles record since no other document type needs a second file.
  const [aadharBackFile, setAadharBackFile] = useState<File | null>(null);
  const [aadharBackError, setAadharBackError] = useState('');
  const aadharBackInput = useRef<HTMLInputElement | null>(null);

  // Aadhaar upload guidance dialog (shown once per session)
  const [aadhaarDialogOpen, setAadhaarDialogOpen] = useState(false);
  const [aadhaarAcknowledged, setAadhaarAcknowledged] = useState(false);
  const aadhaarPendingUpload = useRef(false);

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [reference, setReference] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const detailsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    fetchCsrfToken()
      .then(setCsrfToken)
      .catch(() => setCalendarError(true));
    fetchCalendar()
      .then(setCalendar)
      .catch(() => setCalendarError(true));
  }, []);

  // If the hero widget already collected dates and the user clicked "Check
  // Availability" there, don't make them click it again in here — run the
  // same check immediately so the modal opens straight onto the
  // available/unavailable result instead of back at an empty first step.
  useEffect(() => {
    if (initial?.checkIn && initial?.checkOut) {
      void runAvailabilityCheck(initial.checkIn, initial.checkOut);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Mirrors edits made here back to the hero widget's shared draft, so
  // filling in the modal first (e.g. via a "Book Now" button) leaves the
  // widget showing the same values instead of resetting to blank.
  useEffect(() => {
    onDraftChange?.({ name, checkIn, checkOut, adults, kids });
  }, [name, checkIn, checkOut, adults, kids, onDraftChange]);

  const today = calendar?.serverToday ?? toDateString(new Date());

  const validateStepOne = (ci = checkIn, co = checkOut): boolean => {
    const next: typeof stepErrors = {};
    if (!name.trim()) next.name = 'Please enter your name.';
    if (!ci) next.checkIn = 'Please select a check-in date.';
    else if (ci < today) next.checkIn = 'Check-in date cannot be in the past.';
    if (!co) next.checkOut = 'Please select a check-out date.';
    else if (co < today) next.checkOut = 'Check-out date cannot be in the past.';
    else if (ci && co <= ci) next.checkOut = 'Check-out date must be after check-in date.';
    setStepErrors(next);
    return Object.keys(next).length === 0;
  };

  const runAvailabilityCheck = async (ci: string, co: string) => {
    setChecking(true);
    setSubmitError('');
    let data = calendar;
    if (!data) {
      try {
        data = await fetchCalendar();
        setCalendar(data);
        setCalendarError(false);
      } catch {
        setCalendarError(true);
      }
    }
    const result = checkAvailability(ci, co, data?.bookings ?? [], data?.serverToday ?? today);
    setAvailability(result);
    setChecking(false);
    if (result.available) {
      setTimeout(() => detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
    }
  };

  const handleCheckAvailability = () => {
    setAvailability(null);
    if (!validateStepOne()) return;
    void runAvailabilityCheck(checkIn, checkOut);
  };

  const useDates = (range: DateRange) => {
    setCheckIn(range.checkIn);
    setCheckOut(range.checkOut);
    setStepErrors({});
    void runAvailabilityCheck(range.checkIn, range.checkOut);
  };

  const handleDocFile = (doc: DocType, file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setDocErrors((e) => ({ ...e, [doc]: 'File is larger than 5MB.' }));
      return;
    }
    setDocErrors((e) => ({ ...e, [doc]: undefined }));
    setDocFiles((f) => ({ ...f, [doc]: file }));
  };

  const handleAddDocType = (doc: DocType | '') => {
    if (!doc) return;
    setSelectedDocs((docs) => (docs.includes(doc) ? docs : [...docs, doc]));
  };

  const handleRemoveDocType = (doc: DocType) => {
    setSelectedDocs((docs) => docs.filter((d) => d !== doc));
    setDocNumbers((n) => {
      const next = { ...n };
      delete next[doc];
      return next;
    });
    setDocFiles((f) => {
      const next = { ...f };
      delete next[doc];
      return next;
    });
    setDocErrors((e) => {
      const next = { ...e };
      delete next[doc];
      return next;
    });
    if (doc === 'aadhar') {
      setAadharBackFile(null);
      setAadharBackError('');
    }
  };

  const handleAadharBackFile = (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setAadharBackError('File is larger than 5MB.');
      return;
    }
    setAadharBackError('');
    setAadharBackFile(file);
  };

  const handleRemoveDocFile = (doc: DocType) => {
    setDocFiles((f) => {
      const next = { ...f };
      delete next[doc];
      return next;
    });
    setDocErrors((e) => {
      const next = { ...e };
      delete next[doc];
      return next;
    });
    const input = fileInputs.current[doc];
    if (input) input.value = '';
  };

  const handleRemoveAadharBackFile = () => {
    setAadharBackFile(null);
    setAadharBackError('');
    if (aadharBackInput.current) aadharBackInput.current.value = '';
  };

  const handleUploadClick = (doc: DocType) => {
    if (doc === 'aadhar' && !aadhaarAcknowledged) {
      aadhaarPendingUpload.current = true;
      setAadhaarDialogOpen(true);
      return;
    }
    fileInputs.current[doc]?.click();
  };

  const handleAadhaarFocus = () => {
    if (!aadhaarAcknowledged) {
      aadhaarPendingUpload.current = false;
      setAadhaarDialogOpen(true);
    }
  };

  const proceedAadhaar = () => {
    setAadhaarAcknowledged(true);
    setAadhaarDialogOpen(false);
    if (aadhaarPendingUpload.current) {
      aadhaarPendingUpload.current = false;
      fileInputs.current.aadhar?.click();
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    if (!phone.trim()) {
      setSubmitError('Please enter your phone number.');
      return;
    }
    // The label already marks this required (*), but nothing actually
    // enforced it here -- the backend (public_booking.php) rejects the
    // whole submission if email is empty or invalid, so a guest who
    // skipped it got a generic "please fill in all required fields" only
    // after submitting, with no indication which field was the problem.
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setSubmitError('Please enter a valid email address.');
      return;
    }
    if (adults < 1 || adults > 9) {
      setSubmitError('Maximum 9 adults allowed.');
      return;
    }
    setSubmitting(true);
    try {
      const data = await submitBooking(
        {
          name,
          phone,
          email,
          stateCity,
          checkIn,
          checkOut,
          adults,
          kids,
          message,
          docNumbers,
          docFiles,
          aadharBackFile,
        },
        csrfToken,
      );
      if (data.success) {
        setReference(data.booking_reference ?? '');
        setSuccessMessage(data.message ?? 'Your booking request has been sent.');
        fetchCsrfToken().then(setCsrfToken).catch(() => {});
      } else {
        setSubmitError(data.message ?? 'An error occurred. Please try again.');
      }
    } catch {
      setSubmitError('Connection error. Please check your internet and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <div
      className="fixed inset-0 z-[100] bg-ink/60 backdrop-blur-[2px] overflow-y-auto p-4 md:p-8 flex"
      role="dialog"
      aria-modal="true"
      aria-label="Booking request"
    >
      <div className="bg-paper w-full max-w-[680px] m-auto rounded-xl border border-hairline shadow-[0_24px_64px_rgba(33,32,28,0.35)]">
        {/* Header */}
        <div className="flex items-start justify-between px-7 md:px-9 pt-7 pb-5 border-b border-hairline">
          <div>
            <div className="text-[12px] font-semibold uppercase tracking-[0.12em] text-olive mb-1.5">
              Reserve your stay
            </div>
            <h2 className="font-serif font-medium text-[26px] md:text-[30px] text-ink m-0">Booking Request</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close booking form"
            className="shrink-0 mt-1 w-9 h-9 flex items-center justify-center rounded-full border border-hairline bg-transparent text-muted cursor-pointer transition-colors hover:bg-sand hover:text-ink focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_rgba(75,94,40,0.3)]"
          >
            <X size={17} />
          </button>
        </div>

        {reference || successMessage ? (
          <SuccessPanel reference={reference} message={successMessage} onClose={onClose} />
        ) : (
          <form onSubmit={handleSubmit} noValidate className="px-7 md:px-9 py-7">
            <p className="text-[14.5px] text-muted m-0 mb-4 leading-relaxed">
              Fill in the details below and we'll get back to you shortly via email, phone, or WhatsApp.
            </p>

            <NoteCallout>Check-in 1:00 PM &nbsp;·&nbsp; Check-out 10:00 AM</NoteCallout>

            {calendarError && (
              <div className="mt-3 mb-1 px-3.5 py-3 rounded-md border border-note-border bg-note-bg text-[13px] text-gold-ink-soft">
                Live availability couldn't be loaded right now — you can still send a request and we'll confirm by
                phone or email.
              </div>
            )}

            {/* Step 1 — dates + name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
              <div>
                <label htmlFor="bk-checkin" className={labelClasses}>
                  Check-in date *
                </label>
                <input
                  id="bk-checkin"
                  type="date"
                  min={today}
                  className={inputClasses}
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                />
                {stepErrors.checkIn && <span className={errorClasses}>{stepErrors.checkIn}</span>}
              </div>
              <div>
                <label htmlFor="bk-checkout" className={labelClasses}>
                  Check-out date *
                </label>
                <input
                  id="bk-checkout"
                  type="date"
                  min={checkIn || today}
                  className={inputClasses}
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                />
                {stepErrors.checkOut && <span className={errorClasses}>{stepErrors.checkOut}</span>}
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="bk-name" className={labelClasses}>
                Name *
              </label>
              <input
                id="bk-name"
                type="text"
                className={inputClasses}
                placeholder="Your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              {stepErrors.name && <span className={errorClasses}>{stepErrors.name}</span>}
            </div>

            <div className="mt-5">
              <Button variant="olive" onClick={handleCheckAvailability} disabled={checking} className="w-full">
                {checking ? 'Checking…' : 'Check Availability'}
              </Button>
            </div>

            {/* Availability result */}
            {availability && !availability.available && (
              <UnavailablePanel result={availability} onUseDates={useDates} />
            )}

            {availability?.available && (
              <div className="mt-5 rounded-lg border border-olive/40 bg-[rgba(75,94,40,0.07)] px-5 py-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-olive text-paper flex items-center justify-center">
                  <Check size={22} strokeWidth={3} />
                </div>
                <h3 className="font-serif font-medium text-[21px] text-ink m-0 mb-1">Great news — it's available!</h3>
                <p className="text-[14px] text-muted m-0">
                  {formatDate(checkIn)} → {formatDate(checkOut)} · {availability.requestedDays}{' '}
                  {availability.requestedDays === 1 ? 'night' : 'nights'}. Complete the form below to send your
                  request.
                </p>
              </div>
            )}

            {/* Step 2 — guest details */}
            {availability?.available && (
              <div ref={detailsRef} className="mt-6 pt-6 border-t border-hairline">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="bk-phone" className={labelClasses}>
                      Phone number *
                    </label>
                    <input
                      id="bk-phone"
                      type="tel"
                      className={inputClasses}
                      placeholder="+91 98765 43210"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                    <span className={hintClasses}>Indian or international phone number</span>
                  </div>
                  <div>
                    <label htmlFor="bk-email" className={labelClasses}>
                      Email address *
                    </label>
                    <input
                      id="bk-email"
                      type="email"
                      className={inputClasses}
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <span className={hintClasses}>Booking confirmation will be sent to this email</span>
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="bk-state" className={labelClasses}>
                    State and city
                  </label>
                  <input
                    id="bk-state"
                    type="text"
                    className={inputClasses}
                    placeholder="e.g., Maharashtra, Mumbai"
                    value={stateCity}
                    onChange={(e) => setStateCity(e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label htmlFor="bk-adults" className={labelClasses}>
                      Adults *
                    </label>
                    <NumberInput id="bk-adults" min={1} max={9} className={inputClasses} value={adults} onChange={setAdults} />
                    <span className={hintClasses}>Maximum 9 adults allowed</span>
                  </div>
                  <div>
                    <label htmlFor="bk-kids" className={labelClasses}>
                      Kids (under 12) *
                    </label>
                    <NumberInput id="bk-kids" min={0} className={inputClasses} value={kids} onChange={setKids} />
                  </div>
                </div>

                {/* ID proof */}
                <div className="mt-6">
                  <div className={labelClasses}>ID proof (optional)</div>
                  <p className="text-[13px] text-faint m-0 mb-3">
                    Select a document type to add, then upload the file or enter its number — max 5MB per file (PDF, JPG, PNG, GIF).
                  </p>

                  {docMeta.some(({ key }) => !selectedDocs.includes(key)) && (
                    <select
                      className={inputClasses}
                      value=""
                      onChange={(e) => handleAddDocType(e.target.value as DocType | '')}
                    >
                      <option value="">+ Add a document type…</option>
                      {docMeta
                        .filter(({ key }) => !selectedDocs.includes(key))
                        .map(({ key, label }) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                    </select>
                  )}

                  {selectedDocs.length > 0 && (
                    <div className="flex flex-col gap-3 mt-3.5">
                      {selectedDocs.map((key) => {
                        const label = docMeta.find((d) => d.key === key)!.label;
                        return (
                          <div key={key} className="rounded-lg border border-hairline bg-sand p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="text-[13px] font-semibold text-ink">{label}</div>
                              <button
                                type="button"
                                onClick={() => handleRemoveDocType(key)}
                                aria-label={`Remove ${label}`}
                                className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full border border-hairline bg-transparent text-muted cursor-pointer transition-colors hover:bg-paper hover:text-ink"
                              >
                                <X size={12} />
                              </button>
                            </div>
                            <input
                              type="text"
                              className={cn(inputClasses, 'mb-3 !py-2.5 text-[13.5px] bg-white')}
                              placeholder="Enter number (optional)"
                              value={docNumbers[key] ?? ''}
                              onFocus={key === 'aadhar' ? handleAadhaarFocus : undefined}
                              onChange={(e) => setDocNumbers((n) => ({ ...n, [key]: e.target.value }))}
                            />

                            {key === 'aadhar' ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-faint mb-1.5">
                                    Front side
                                  </div>
                                  <DocFileSlot
                                    file={docFiles.aadhar}
                                    error={docErrors.aadhar}
                                    uploadLabel="Upload front side"
                                    inputRef={(el) => {
                                      fileInputs.current.aadhar = el;
                                    }}
                                    onPick={() => handleUploadClick('aadhar')}
                                    onFileChange={(file) => handleDocFile('aadhar', file)}
                                    onRemove={() => handleRemoveDocFile('aadhar')}
                                  />
                                </div>
                                <div>
                                  <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-faint mb-1.5">
                                    Back side
                                  </div>
                                  <DocFileSlot
                                    file={aadharBackFile}
                                    error={aadharBackError}
                                    uploadLabel="Upload back side"
                                    inputRef={(el) => {
                                      aadharBackInput.current = el;
                                    }}
                                    onPick={() => aadharBackInput.current?.click()}
                                    onFileChange={handleAadharBackFile}
                                    onRemove={handleRemoveAadharBackFile}
                                  />
                                </div>
                              </div>
                            ) : (
                              <DocFileSlot
                                file={docFiles[key]}
                                error={docErrors[key]}
                                inputRef={(el) => {
                                  fileInputs.current[key] = el;
                                }}
                                onPick={() => handleUploadClick(key)}
                                onFileChange={(file) => handleDocFile(key, file)}
                                onRemove={() => handleRemoveDocFile(key)}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="mt-5">
                  <label htmlFor="bk-message" className={labelClasses}>
                    Message (optional)
                  </label>
                  <textarea
                    id="bk-message"
                    rows={4}
                    className={inputClasses}
                    placeholder="Any special requests or questions?"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>

                {submitError && (
                  <div className="mt-4 px-4 py-3 rounded-md border border-[#D9A79C] bg-[rgba(155,58,46,0.07)] text-[13.5px] text-[#8A2F25]">
                    {submitError}
                  </div>
                )}

                <div className="mt-5">
                  <Button type="submit" disabled={submitting} className="w-full">
                    {submitting ? 'Sending…' : 'Send Booking Request'}
                  </Button>
                </div>
              </div>
            )}
          </form>
        )}
      </div>
    </div>

      {/* Aadhaar upload guidance dialog — sibling of the main overlay so `fixed`
          positioning isn't trapped by its backdrop-filter containing block */}
      {aadhaarDialogOpen && (
        <div className="fixed inset-0 z-[110] bg-ink/50 flex overflow-y-auto p-4">
          <div className="bg-paper w-full max-w-[480px] m-auto rounded-xl border border-hairline shadow-[0_24px_64px_rgba(33,32,28,0.35)]">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-hairline">
              <h3 className="font-serif font-medium text-[20px] text-ink m-0">Aadhar Card Upload</h3>
              <button
                type="button"
                onClick={() => setAadhaarDialogOpen(false)}
                aria-label="Close"
                className="w-8 h-8 flex items-center justify-center rounded-full border border-hairline bg-transparent text-muted cursor-pointer transition-colors hover:bg-sand hover:text-ink"
              >
                <X size={15} />
              </button>
            </div>
            <div className="px-6 py-5">
              <NoteCallout>Please read carefully before uploading.</NoteCallout>
              <ul className="m-0 mt-3 pl-5 text-[14px] text-ink-soft leading-relaxed">
                <li>
                  Upload <strong>one combined PDF</strong> with both sides, or use the separate front/back upload
                  buttons below
                </li>
                <li>Maximum file size: <strong>5MB per file</strong></li>
                <li>Ensure the document is clear and readable</li>
                <li>All corners of the card must be visible</li>
              </ul>
              <p className="text-[13px] text-muted mt-3.5 mb-0">
                Tip: if you'd rather send one file, use a mobile scanner app or PDF merger tool to combine both
                sides into a single PDF.
              </p>
            </div>
            <div className="px-6 pb-5 flex justify-end">
              <Button onClick={proceedAadhaar}>Got it, proceed</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DocFileSlot({
  file,
  error,
  uploadLabel = 'Upload file',
  inputRef,
  onPick,
  onFileChange,
  onRemove,
}: {
  file: File | null | undefined;
  error?: string;
  uploadLabel?: string;
  inputRef: (el: HTMLInputElement | null) => void;
  onPick: () => void;
  onFileChange: (file: File | undefined) => void;
  onRemove: () => void;
}) {
  return (
    <div>
      <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.gif" className="hidden" onChange={(e) => onFileChange(e.target.files?.[0])} />
      {file ? (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-md border border-olive/30 bg-white">
          <Check size={13} className="text-olive shrink-0" />
          <span className="flex-1 min-w-0 text-[12.5px] text-ink-soft truncate">{file.name}</span>
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove file"
            className="shrink-0 w-5 h-5 flex items-center justify-center rounded-full text-muted cursor-pointer transition-colors hover:bg-sand hover:text-[#8A2F25]"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={onPick}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md border border-hairline bg-paper text-[13px] font-semibold text-ink-soft cursor-pointer transition-colors hover:border-olive hover:text-olive focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_rgba(75,94,40,0.3)]"
        >
          <Upload size={14} />
          {uploadLabel}
        </button>
      )}
      {error && <span className={errorClasses}>{error}</span>}
    </div>
  );
}

function UnavailablePanel({
  result,
  onUseDates,
}: {
  result: AvailabilityResult;
  onUseDates: (range: DateRange) => void;
}) {
  const nightsLabel = (n: number) => `${n} ${n === 1 ? 'night' : 'nights'}`;

  return (
    <div className="mt-5">
      <div className="rounded-lg border border-[#D9A79C] bg-[rgba(155,58,46,0.06)] px-5 py-4">
        <h3 className="font-serif font-medium text-[18px] text-[#8A2F25] m-0 mb-1">Those dates aren't available</h3>
        <p className="text-[13.5px] text-[#8A2F25]/85 m-0">{result.reason}</p>
      </div>

      {result.nextAvailable && (
        <div className="mt-3.5 rounded-lg border border-note-border bg-note-bg px-5 py-4">
          <div className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.1em] text-gold-press mb-1.5">
            <Sparkles size={13} /> Best match for {nightsLabel(result.requestedDays)}
          </div>
          <p className="text-[14.5px] text-gold-ink m-0 mb-3">
            {formatDate(result.nextAvailable.checkIn)} → {formatDate(result.nextAvailable.checkOut)}
          </p>
          <Button variant="olive" className="!px-5 !py-2.5 !text-[13px]" onClick={() => onUseDates(result.nextAvailable!)}>
            Use these dates
          </Button>
        </div>
      )}

      {result.alternatives && result.alternatives.length > 0 && (
        <div className="mt-3.5">
          <div className="text-[12px] font-semibold uppercase tracking-[0.1em] text-faint mb-2">
            Shorter stays near your dates
          </div>
          <div className="flex flex-col gap-2.5">
            {result.alternatives.map((alt) => (
              <div
                key={alt.checkIn}
                className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-hairline bg-white px-4 py-3"
              >
                <div>
                  <div className="text-[14px] text-ink font-semibold">
                    {formatDate(alt.checkIn)} → {formatDate(alt.checkOut)}
                  </div>
                  <div className="text-[12.5px] text-faint">{nightsLabel(alt.days)}</div>
                </div>
                <Button variant="outline" className="!px-4.5 !py-2 !text-[12.5px]" onClick={() => onUseDates(alt)}>
                  Use these dates
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SuccessPanel({ reference, message, onClose }: { reference: string; message: string; onClose: () => void }): ReactNode {
  return (
    <div className="px-7 md:px-9 py-10 text-center">
      <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-olive text-paper flex items-center justify-center">
        <Check size={26} strokeWidth={3} />
      </div>
      <h3 className="font-serif font-medium text-[24px] text-ink m-0 mb-2">Request sent!</h3>
      <p className="text-[15px] text-muted m-0 mb-1 leading-relaxed">{message}</p>
      {reference && (
        <p className="text-[14px] text-ink m-0 mb-6">
          Reference: <strong className="text-olive">{reference}</strong>
        </p>
      )}
      <Button variant="outline" onClick={onClose}>
        Done
      </Button>
    </div>
  );
}
